import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { beginImpersonation, homePathFor } from '../../utils/impersonation';
import './impersonation.scss';

/**
 * ImpersonateLanding — `/impersonate?ticket=…`, the institute-origin half of a
 * platform support login.
 *
 * PUBLIC by necessity, exactly like WhatsAppConnectRelay: the platform operator
 * who started this has no session on THIS origin (their console token lives in
 * lms.lmscampus.com's localStorage). The one-time ticket in the URL is the whole
 * credential, and it is spent here.
 *
 * The ticket is stripped from the address bar before anything else renders, so
 * it does not sit in browser history or leak through a Referer header. It is
 * single-use and server-side expiring anyway, but a spent ticket in the URL bar
 * is still an invitation to paste it somewhere.
 */
const ImpersonateLanding = () => {
  const [params] = useSearchParams();
  const [error, setError] = useState('');
  // StrictMode double-invokes effects in dev; a single-use ticket would be
  // burned by the first call and rejected by the second.
  const redeemed = useRef(false);

  useEffect(() => {
    const ticket = params.get('ticket');

    // Drop the ticket from the visible URL immediately, before any await.
    if (ticket && window.history?.replaceState) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (!ticket) {
      setError('This support link is missing its ticket. Start a new session from the institute page.');
      return;
    }
    if (redeemed.current) return;
    redeemed.current = true;

    (async () => {
      try {
        const { data } = await apiClient.post('/auth/impersonate/redeem', { ticket });
        beginImpersonation(data);
        // Hard navigation, not react-router. This page was loaded with NO token,
        // so PlanContext / BrandingContext / SocketContext all booted empty; a
        // client-side transition would leave them that way and the operator
        // would be looking at an app with no plan features — a fake bug on top
        // of the real one they came to diagnose.
        window.location.replace(homePathFor(data.user?.usertype));
      } catch (err) {
        setError(
          err.response?.data?.message ||
            'This support link could not be used. It may have expired or already been opened.'
        );
      }
    })();
  }, [params]);

  return (
    <div className="imp-landing">
      <div className="imp-landing__card">
        {error ? (
          <>
            <AlertTriangle size={32} className="imp-landing__icon imp-landing__icon--error" />
            <h1 className="imp-landing__title">Support session not started</h1>
            <p className="imp-landing__text">{error}</p>
          </>
        ) : (
          <>
            <ShieldCheck size={32} className="imp-landing__icon" />
            <h1 className="imp-landing__title">Starting support session…</h1>
            <p className="imp-landing__text">Verifying the one-time link and signing you in.</p>
            <div className="imp-landing__bar"><span /></div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImpersonateLanding;
